import { authClient } from '@/lib/auth-client';

// Define permission resources and actions
export type PermissionResource =
  | 'user'
  | 'session'
  | 'organization'
  | 'apiKey'
  | 'project'
  | 'asset';

export type PermissionAction =
  | 'create'
  | 'read'
  | 'update'
  | 'delete'
  | 'list'
  | 'ban'
  | 'set-role'
  | 'impersonate'
  | 'revoke'
  | 'share';

export interface PermissionCheck {
  [resource: string]: PermissionAction[];
}

// Permission service class to handle all permission-related operations
class PermissionService {
  private permissionCache: Map<string, boolean> = new Map();

  // Check if a user has specific permissions
  async hasPermission(permissions: PermissionCheck): Promise<boolean> {
    try {
      const result = await authClient.admin.hasPermission({
        permissions,
      });

      // The result is wrapped in Data<{ error: null, success: boolean }> | Error
      if ('error' in result && result.error) {
        console.error('Permission check error:', result.error);
        return false;
      }

      return !!result?.data?.success;
    } catch (error) {
      console.error('Error checking permissions:', error);
      return false;
    }
  }

  // Check if user is an admin
  async isAdmin(): Promise<boolean> {
    // Try to use cached value first
    const cacheKey = 'isAdmin';
    const cached = this.permissionCache.get(cacheKey);
    if (cached !== undefined) {
      return cached;
    }

    try {
      const result = await authClient.admin.hasPermission({
        permissions: {
          user: ['list'], // A basic admin permission to check
        },
      });

      // The result is wrapped in Data<{ error: null, success: boolean }> | Error
      if ('error' in result && result.error) {
        console.error('Admin check error:', result.error);
        this.permissionCache.set(cacheKey, false);
        return false;
      }

      const isAdmin = !!result?.data?.success;
      this.permissionCache.set(cacheKey, isAdmin);
      return isAdmin;
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  }

  // Check if user can perform a specific action on a resource
  async can(
    resource: PermissionResource,
    action: PermissionAction
  ): Promise<boolean> {
    const cacheKey = `${resource}:${action}`;
    const cached = this.permissionCache.get(cacheKey);
    if (cached !== undefined) {
      return cached;
    }

    try {
      const result = await authClient.admin.hasPermission({
        permissions: {
          [resource]: [action],
        },
      });

      // The result is wrapped in Data<{ error: null, success: boolean }> | Error
      if ('error' in result && result.error) {
        console.error(`Permission check error for ${resource}:${action}:`, result.error);
        this.permissionCache.set(cacheKey, false);
        return false;
      }

      const canPerform = !!result?.data?.success;
      this.permissionCache.set(cacheKey, canPerform);
      return canPerform;
    } catch (error) {
      console.error(`Error checking permission for ${resource}:${action}:`, error);
      return false;
    }
  }

  // Clear the permission cache (useful when user session changes)
  clearCache(): void {
    this.permissionCache.clear();
  }

  // Check multiple permissions at once
  async hasMultiplePermissions(permissions: PermissionCheck): Promise<boolean> {
    try {
      const result = await authClient.admin.hasPermission({
        permissions,
      });

      // The result is wrapped in Data<{ error: null, success: boolean }> | Error
      if ('error' in result && result.error) {
        console.error('Multiple permissions check error:', result.error);
        return false;
      }

      return !!result?.data?.success;
    } catch (error) {
      console.error('Error checking multiple permissions:', error);
      return false;
    }
  }

  // ===== ASSET PERMISSION METHODS =====

  /**
   * Check if user can perform a specific action on assets in their organization.
   * Uses Better Auth's organization permission system.
   */
  async canPerformAssetAction(
    action: 'create' | 'read' | 'update' | 'delete' | 'share'
  ): Promise<boolean> {
    const cacheKey = `asset:${action}`;
    const cached = this.permissionCache.get(cacheKey);
    if (cached !== undefined) {
      return cached;
    }

    try {
      const result = await authClient.organization.hasPermission({
        permissions: {
          asset: [action],
        },
      });

      // The result is wrapped in Data<{ error: null, success: boolean }> | Error
      if ('error' in result && result.error) {
        console.error(`Asset ${action} permission check error:`, result.error);
        this.permissionCache.set(cacheKey, false);
        return false;
      }

      const canPerform = !!result?.data?.success;
      this.permissionCache.set(cacheKey, canPerform);
      return canPerform;
    } catch (error) {
      console.error(`Error checking asset ${action} permission:`, error);
      return false;
    }
  }

  /**
   * Get all asset permissions for the current user in their active organization.
   * Returns an object with permission flags for common asset operations.
   */
  async getAssetPermissions(): Promise<{
    canCreate: boolean;
    canRead: boolean;
    canUpdate: boolean;
    canDelete: boolean;
    canShare: boolean;
  }> {
    const [canCreate, canRead, canUpdate, canDelete, canShare] = await Promise.all([
      this.canPerformAssetAction('create'),
      this.canPerformAssetAction('read'),
      this.canPerformAssetAction('update'),
      this.canPerformAssetAction('delete'),
      this.canPerformAssetAction('share'),
    ]);

    return { canCreate, canRead, canUpdate, canDelete, canShare };
  }

  /**
   * Client-side role permission check (synchronous, no API call).
   * Use for quick UI decisions. For authoritative checks, use canPerformAssetAction().
   *
   * @param role - The role to check permissions for
   * @param action - The action to check permission for
   * @returns boolean indicating if the role has permission
   */
  checkAssetRolePermission(
    role: 'owner' | 'admin' | 'member' | 'viewer',
    action: 'create' | 'read' | 'update' | 'delete' | 'share'
  ): boolean {
    return authClient.organization.checkRolePermission({
      permissions: { asset: [action] },
      role,
    });
  }
}

// Create a singleton instance of the permission service
export const permissionService = new PermissionService();